import {Component, ViewChild} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {TreeNode} from 'primeng/api';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {CpmCLocationApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-location.api-model';
import {
    NewEditLocationModalComponent,
    NewEditLocationModalConfig,
    NewEditLocationModalResult
} from '../../modals/new-edit-location-modal/new-edit-location-modal.component';
import {CpmCLocationApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-location.api-service';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {firstValueFrom} from 'rxjs';
import {ECpmCLocationTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-location.api-enums';
import {LocationUtils} from '../../utils/location.util';
import {Tree} from 'primeng/tree';
import {TreeUtils} from '../../../../sdk/src/utils/tree.utils';
import {CpmCLocationTreeApiView} from '../../../../gen/cpm-c-api-ts/src/views/c-customer/cpmc-location-tree.api-view';

/**
 * Location hierarchy tree component
 */
@Component({
    selector: 'app-location-hierarchy-tree',
    templateUrl: './location-hierarchy-tree.component.html',
    styleUrl: './location-hierarchy-tree.component.scss',
    imports: [PrimeNgImportsModule]
})
export class LocationHierarchyTreeComponent extends IBaseCRUDComponent<
    CpmCLocationApiModel,
    NewEditLocationModalComponent,
    NewEditLocationModalConfig,
    NewEditLocationModalResult
> {
    protected readonly LocationHierarchyTreeComponent = LocationHierarchyTreeComponent;

    // ===============================================================
    // ===============================================================

    @ViewChild(Tree)
    public tree?: Tree;

    // ===============================================================
    // ===============================================================

    public static instance?: LocationHierarchyTreeComponent;
    public static readonly treeItemRoot: TreeNode<CpmCLocationTreeApiView> = {children: []};
    public static activeUUIDPath?: string;

    // ===============================================================
    // ===============================================================

    public static onLocationsLoaded?: () => Promise<void>;
    public static onLocationClick?: (
        locationTree: CpmCLocationTreeApiView,
        locationTreeNode: TreeNode<CpmCLocationTreeApiView>
    ) => Promise<void>;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'Location',
            getModelHumanIdentifier: (model: CpmCLocationApiModel) => model.name ?? '<Unknown location>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCLocationApiModel) =>
                CpmCLocationApiService.deleteLocation(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditLocationModalComponent,
            getDialogServiceParameters: NewEditLocationModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCLocationApiModel) => new NewEditLocationModalConfig(model),

            // Authorization config
            authorizationNew: CpmCLocationApiService.endpointNew(this.FAKE_UUID, this.FAKE_UUID),
            authorizationEdit: CpmCLocationApiService.endpointEdit(this.FAKE_UUID, this.FAKE_UUID, this.FAKE_UUID),
            authorizationDelete: CpmCLocationApiService.endpointDelete(this.FAKE_UUID, this.FAKE_UUID, this.FAKE_UUID)
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        LocationHierarchyTreeComponent.instance = this;
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        LocationHierarchyTreeComponent.instance = undefined;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    protected override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading locations...';

        const expandedSnapshot: string[] = [];
        TreeUtils.treeNodesSnapshotExpanded(
            this.LocationHierarchyTreeComponent.treeItemRoot.children!,
            expandedSnapshot
        );

        const locationsTree = (
            await firstValueFrom(CpmCLocationApiService.getLocationTree({}, CpmCLocationApiService.dPaginatorConfig))
        ).data;
        this.LocationHierarchyTreeComponent.treeItemRoot.children = [];
        LocationUtils.buildLocationHierarchyTree(
            locationsTree,
            LocationHierarchyTreeComponent.treeItemRoot,
            [ECpmCLocationTypeApiEnum.REGION],
            []
        );

        TreeUtils.treeNodesRestoreExpanded(
            this.LocationHierarchyTreeComponent.treeItemRoot.children!,
            expandedSnapshot,
            true
        );
        this.tree?.updateSerializedValue();

        if (LocationHierarchyTreeComponent.onLocationsLoaded) LocationHierarchyTreeComponent.onLocationsLoaded();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Auto expand active locations
     */
    public autoExpandActiveLocations() {
        const lastFoundTreeNode = TreeUtils.treeNodesExpandByKeys(
            LocationHierarchyTreeComponent.treeItemRoot.children!,
            LocationHierarchyTreeComponent.activeUUIDPath?.split('/').filter((x) => x.length > 0) ?? []
        );
        this.tree?.updateSerializedValue();

        if (lastFoundTreeNode) {
            const visibleIndex = TreeUtils.treeNodeGetVisibleIndex(
                LocationHierarchyTreeComponent.treeItemRoot.children!,
                lastFoundTreeNode
            );

            setTimeout(() => {
                this.tree?.scrollToVirtualIndex(visibleIndex);
            }, 0);
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * When a location is clicked
     * @param locationTree Clicked location tree
     * @param locationTreeNode Clicked location tree node
     */
    public async selectLocation(
        locationTree: CpmCLocationTreeApiView,
        locationTreeNode: TreeNode<CpmCLocationTreeApiView>
    ) {
        this.showLoading = true;
        this.loadingStr = `Loading ${locationTree.name}`;
        await LocationHierarchyTreeComponent.onLocationClick?.(locationTree, locationTreeNode);
        this.showLoading = false;
    }
    /**
     * De-select current location
     */
    public deselectLocation() {
        LocationHierarchyTreeComponent.activeUUIDPath = '';
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        // Nothing to do here
    }
}
